// ═══════════════════════════════════════════════════════════════════════════
// MODULE MSV COMPLET - MARAÎCHAGE SUR SOL VIVANT
// ═══════════════════════════════════════════════════════════════════════════
// Farm-Booking - Onglet MSV indépendant
// 73+ Papy / Joshua - Janvier 2026
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calendar, CheckCircle2, Circle, ChevronRight, ChevronDown, ChevronLeft,
  Leaf, Droplets, Sun, Snowflake, Clock, Target, AlertTriangle, Plus,
  TrendingUp, MapPin, Sprout, TreeDeciduous, BarChart3, Settings,
  Eye, Edit, Save, X, RefreshCw, Search, Filter, Download,
  Thermometer, Bug, CloudRain, Layers, Book, Truck, Coins
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION API
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = '/api/msv';

const fetchAPI = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error(`Erreur API ${endpoint}:`, error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANTS UTILITAIRES
// ═══════════════════════════════════════════════════════════════════════════

const StatBadge = ({ label, value, color }) => {
  const colors = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    orange: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className={`px-3 py-1 rounded-full ${colors[color]}`}>
      <span className="text-xs opacity-75">{label}:</span>{' '}
      <span className="font-bold">{value}</span>
    </div>
  );
};

const ProgressBar = ({ value, max, color = '#22c55e', height = 8 }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full overflow-hidden" style={{ height }}>
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
};

const Card = ({ children, className = '', onClick }) => (
  <div
    className={`bg-white rounded-xl shadow-md p-6 ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''} ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const ModuleMSV = () => {
  // État global
  const [vueActive, setVueActive] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Données
  const [config, setConfig] = useState(null);
  const [phases, setPhases] = useState([]);
  const [jardins, setJardins] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [cultures, setCultures] = useState([]);
  const [stats, setStats] = useState(null);

  // Sélections
  const [jardinSelectionne, setJardinSelectionne] = useState(null);
  const [anneeActive, setAnneeActive] = useState(new Date().getFullYear());

  // Chargement initial
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [configData, phasesData, jardinsData, famillesData, culturesData, statsData] = await Promise.all([
          fetchAPI('/config'),
          fetchAPI('/phases'),
          fetchAPI('/jardins'),
          fetchAPI('/familles'),
          fetchAPI('/cultures'),
          fetchAPI('/stats'),
        ]);
        setConfig(configData);
        setPhases(phasesData);
        setJardins(jardinsData);
        setFamilles(famillesData);
        setCultures(culturesData);
        setStats(statsData);
      } catch (err) {
        setError('Erreur de chargement des données MSV');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Rafraîchir les données
  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const [jardinsData, statsData] = await Promise.all([
        fetchAPI('/jardins'),
        fetchAPI('/stats'),
      ]);
      setJardins(jardinsData);
      setStats(statsData);
    } catch (err) {
      console.error('Erreur refresh:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Navigation
  const navigation = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'timeline', label: 'Timeline', icon: TrendingUp },
    { id: 'jardins', label: 'Jardins', icon: MapPin },
    { id: 'checklist', label: 'Checklist', icon: CheckCircle2 },
    { id: 'rotation', label: 'Rotation', icon: RefreshCw },
    { id: 'cultures', label: 'Cultures', icon: Sprout },
    { id: 'matieres', label: 'Matières', icon: Layers },
  ];

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement du module MSV...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <Leaf className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">🌱 Maraîchage Sol Vivant</h1>
                <p className="text-sm text-gray-500">{config?.ferme_nom} - {config?.commune}</p>
              </div>
            </div>

            {/* Stats rapides */}
            <div className="hidden md:flex items-center gap-6">
              <StatBadge
                label="Progression"
                value={`${Math.round((stats?.progression?.jardins_convertis || 0) / (stats?.progression?.total_jardins || 1) * 100)}%`}
                color="green"
              />
              <StatBadge
                label="Jardins MSV"
                value={`${stats?.progression?.jardins_convertis || 0}/${stats?.progression?.total_jardins || 0}`}
                color="blue"
              />
              <StatBadge
                label="Tâches"
                value={`${stats?.taches?.faites || 0}/${stats?.taches?.total || 0}`}
                color="purple"
              />
            </div>

            <button
              onClick={refreshData}
              className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex gap-1 mt-4 overflow-x-auto pb-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setVueActive(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    vueActive === item.id
                      ? 'bg-green-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-green-50 hover:text-green-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {vueActive === 'dashboard' && (
          <VueDashboard stats={stats} phases={phases} jardins={jardins} />
        )}
        {vueActive === 'timeline' && (
          <VueTimeline phases={phases} jardins={jardins} onSelectJardin={setJardinSelectionne} />
        )}
        {vueActive === 'jardins' && (
          <VueJardins
            jardins={jardins}
            phases={phases}
            jardinSelectionne={jardinSelectionne}
            onSelectJardin={setJardinSelectionne}
            onRefresh={refreshData}
          />
        )}
        {vueActive === 'checklist' && (
          <VueChecklist
            jardins={jardins}
            jardinSelectionne={jardinSelectionne}
            onSelectJardin={setJardinSelectionne}
            annee={anneeActive}
            onRefresh={refreshData}
          />
        )}
        {vueActive === 'rotation' && (
          <VueRotation jardins={jardins} familles={familles} anneeActive={anneeActive} />
        )}
        {vueActive === 'cultures' && (
          <VueCultures cultures={cultures} familles={familles} />
        )}
        {vueActive === 'matieres' && (
          <VueMatieres />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          🌱 Module MSV - Transition {config?.annee_debut_transition}-{config?.annee_fin_transition} | 
          🪱 Sol Vivant = Sol Heureux | 73+ Papy / Joshua
        </div>
      </footer>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════

const VueDashboard = ({ stats, phases, jardins }) => {
  const progressionGlobale = stats?.progression
    ? Math.round((stats.progression.jardins_convertis / stats.progression.total_jardins) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Stats principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Progression MSV</p>
              <p className="text-3xl font-bold">{progressionGlobale}%</p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-50" />
          </div>
          <ProgressBar value={progressionGlobale} max={100} color="rgba(255,255,255,0.5)" height={6} />
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Jardins convertis</p>
              <p className="text-3xl font-bold text-blue-600">
                {stats?.progression?.jardins_convertis || 0}
                <span className="text-lg text-gray-400">/{stats?.progression?.total_jardins || 0}</span>
              </p>
            </div>
            <MapPin className="w-10 h-10 text-blue-200" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Planches MSV</p>
              <p className="text-3xl font-bold text-purple-600">
                {stats?.planches?.msv || 0}
                <span className="text-lg text-gray-400">/{stats?.planches?.total || 0}</span>
              </p>
            </div>
            <Layers className="w-10 h-10 text-purple-200" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Tâches {stats?.annee_courante}</p>
              <p className="text-3xl font-bold text-orange-600">
                {stats?.taches?.faites || 0}
                <span className="text-lg text-gray-400">/{stats?.taches?.total || 0}</span>
              </p>
            </div>
            <CheckCircle2 className="w-10 h-10 text-orange-200" />
          </div>
        </Card>
      </div>

      {/* Phases */}
      <Card>
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-green-600" />
          Phases de transition
        </h2>
        <div className="space-y-3">
          {phases.map((phase) => (
            <div key={phase.id} className="flex items-center gap-4">
              <div
                className="w-16 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: phase.couleur_hex }}
              >
                {phase.annee}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800">{phase.nom}</span>
                  <span className="text-sm text-gray-500">{phase.pct_conversion || 0}%</span>
                </div>
                <ProgressBar
                  value={phase.pct_conversion || 0}
                  max={100}
                  color={phase.couleur_hex}
                  height={6}
                />
              </div>
              <div className="text-sm text-gray-500 w-20 text-right">
                {phase.jardins_convertis || 0}/{phase.nb_jardins} jardins
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Dernières observations */}
      {stats?.dernieres_observations?.length > 0 && (
        <Card>
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-green-600" />
            Dernières observations
          </h2>
          <div className="space-y-2">
            {stats.dernieres_observations.map((obs, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center font-bold text-green-700">
                  {obs.jardin}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    Vers: <span className="font-medium">{obs.vers_de_terre_qualite || '-'}</span>
                    {' • '}
                    Cultures: <span className="font-medium">{obs.etat_cultures || '-'}</span>
                  </p>
                  <p className="text-xs text-gray-400">{obs.date_observation}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE TIMELINE
// ═══════════════════════════════════════════════════════════════════════════

const VueTimeline = ({ phases, jardins, onSelectJardin }) => {
  const [phaseExpanded, setPhaseExpanded] = useState(phases[0]?.id);

  return (
    <Card>
      <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-green-600" />
        Timeline de Transition MSV (5 ans)
      </h2>

      <div className="space-y-4">
        {phases.map((phase, index) => {
          const jardinsPhase = jardins.filter((j) => j.phase_id === phase.id);
          const isExpanded = phaseExpanded === phase.id;

          return (
            <div key={phase.id} className="relative">
              {/* Ligne de connexion */}
              {index < phases.length - 1 && (
                <div className="absolute left-8 top-full w-0.5 h-4 bg-gray-300 z-0" />
              )}

              <div
                onClick={() => setPhaseExpanded(isExpanded ? null : phase.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isExpanded
                    ? 'border-green-500 bg-green-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                    style={{ backgroundColor: phase.couleur_hex }}
                  >
                    {phase.annee}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-800 text-lg">{phase.nom}</h3>
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                        Phase {phase.id}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{phase.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="flex items-center gap-1 text-green-600">
                        <Sprout className="w-4 h-4" />
                        {jardinsPhase.length} jardins
                      </span>
                      <span className="flex items-center gap-1 text-blue-600">
                        <Target className="w-4 h-4" />
                        {phase.objectif_pct}% ferme
                      </span>
                    </div>
                  </div>

                  <div className="w-32">
                    <div className="text-sm text-gray-500 mb-1 text-right">
                      {phase.pct_conversion || 0}%
                    </div>
                    <ProgressBar
                      value={phase.pct_conversion || 0}
                      max={100}
                      color={phase.couleur_hex}
                    />
                  </div>

                  <ChevronRight
                    className={`w-6 h-6 transition-transform ${
                      isExpanded ? 'rotate-90 text-green-600' : 'text-gray-400'
                    }`}
                  />
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <div className="grid grid-cols-4 gap-3">
                      {jardinsPhase.map((jardin) => {
                        const progression = jardin.taches_total > 0
                          ? Math.round((jardin.taches_faites / jardin.taches_total) * 100)
                          : 0;

                        return (
                          <button
                            key={jardin.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectJardin(jardin);
                            }}
                            className="p-3 bg-white rounded-lg border border-green-300 hover:border-green-500 hover:shadow transition-all text-center"
                          >
                            <div
                              className="text-2xl font-bold"
                              style={{ color: phase.couleur_hex }}
                            >
                              {jardin.code}
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                              {jardin.nb_planches} planches
                            </div>
                            <ProgressBar
                              value={progression}
                              max={100}
                              color={phase.couleur_hex}
                              height={4}
                            />
                            <div className="text-xs text-gray-400 mt-1">{progression}%</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE JARDINS
// ═══════════════════════════════════════════════════════════════════════════

const VueJardins = ({ jardins, phases, jardinSelectionne, onSelectJardin, onRefresh }) => {
  const jardinsPleinChamp = jardins.filter((j) => j.type === 'plein_champ');
  const serres = jardins.filter((j) => j.type === 'serre');

  const getPhaseColor = (phaseId) => {
    const phase = phases.find((p) => p.id === phaseId);
    return phase?.couleur_hex || '#gray';
  };

  const renderJardinCard = (jardin) => {
    const progression = jardin.taches_total > 0
      ? Math.round((jardin.taches_faites / jardin.taches_total) * 100)
      : 0;
    const color = getPhaseColor(jardin.phase_id);
    const isSelected = jardinSelectionne?.id === jardin.id;

    return (
      <button
        key={jardin.id}
        onClick={() => onSelectJardin(jardin)}
        className={`relative p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
          isSelected ? 'ring-4 ring-green-400' : ''
        }`}
        style={{
          borderColor: color,
          backgroundColor: `${color}10`,
        }}
      >
        {/* Badge phase */}
        <div
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold"
          style={{ backgroundColor: color }}
        >
          {jardin.phase_id}
        </div>

        {/* Badge témoin */}
        {jardin.est_temoin && (
          <div className="absolute -top-2 -left-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
            🧪
          </div>
        )}

        <div className="text-2xl font-bold mb-1" style={{ color }}>
          {jardin.type === 'serre' ? '🏠 ' : ''}{jardin.code}
        </div>
        <div className="text-xs text-gray-500 mb-2">{jardin.nb_planches} planches</div>

        <ProgressBar value={progression} max={100} color={color} height={6} />
        <div className="text-xs text-gray-400 mt-1">{progression}%</div>

        {/* Statut */}
        <div className="absolute top-2 left-2">
          {jardin.statut_msv === 'converti' ? (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          ) : jardin.statut_msv === 'en_transition' ? (
            <Clock className="w-4 h-4 text-orange-500" />
          ) : (
            <Circle className="w-4 h-4 text-gray-300" />
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Liste des jardins */}
      <div className="lg:col-span-2 space-y-6">
        {/* Plein champ */}
        <Card>
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-500" />
            Plein Champ ({jardinsPleinChamp.length} jardins - {jardinsPleinChamp.length * 8} planches)
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {jardinsPleinChamp.map(renderJardinCard)}
          </div>
        </Card>

        {/* Serres */}
        <Card>
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TreeDeciduous className="w-5 h-5 text-red-500" />
            Serres ({serres.length} serres - {serres.length * 8} planches)
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {serres.map(renderJardinCard)}
          </div>
        </Card>

        {/* Légende */}
        <div className="flex flex-wrap gap-4 justify-center text-sm">
          {phases.map((phase) => (
            <div key={phase.id} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: phase.couleur_hex }}
              />
              <span className="text-gray-600">
                Phase {phase.id}: {phase.nom}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Détail jardin sélectionné */}
      <div>
        {jardinSelectionne ? (
          <JardinDetail jardin={jardinSelectionne} phases={phases} onRefresh={onRefresh} />
        ) : (
          <Card className="text-center py-12">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Sélectionnez un jardin pour voir les détails</p>
          </Card>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DÉTAIL JARDIN
// ═══════════════════════════════════════════════════════════════════════════

const JardinDetail = ({ jardin, phases, onRefresh }) => {
  const [planches, setPlanches] = useState([]);
  const [loading, setLoading] = useState(true);

  const phase = phases.find((p) => p.id === jardin.phase_id);
  const progression = jardin.taches_total > 0
    ? Math.round((jardin.taches_faites / jardin.taches_total) * 100)
    : 0;

  useEffect(() => {
    const loadPlanches = async () => {
      setLoading(true);
      try {
        const data = await fetchAPI(`/jardins/${jardin.id}`);
        setPlanches(data.planches || []);
      } catch (err) {
        console.error('Erreur chargement planches:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPlanches();
  }, [jardin.id]);

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 pb-4 border-b">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
          style={{ backgroundColor: phase?.couleur_hex }}
        >
          {jardin.code}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-800">{jardin.nom}</h2>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>{jardin.nb_planches} planches</span>
            <span>•</span>
            <span>{jardin.surface_totale_m2} m²</span>
            <span>•</span>
            <span style={{ color: phase?.couleur_hex }}>
              Phase {phase?.id} ({phase?.annee})
            </span>
          </div>
        </div>
      </div>

      {/* Progression */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">Progression checklist</span>
          <span className="font-bold" style={{ color: phase?.couleur_hex }}>
            {progression}%
          </span>
        </div>
        <ProgressBar value={progression} max={100} color={phase?.couleur_hex} height={8} />
      </div>

      {/* Infos sol */}
      <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Sol
        </h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500">Type:</span>{' '}
            <span className="font-medium">{jardin.sol_type || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Historique:</span>{' '}
            <span className="font-medium">{jardin.historique_cultural || '-'}</span>
          </div>
          {jardin.sol_ph && (
            <div>
              <span className="text-gray-500">pH:</span>{' '}
              <span className="font-medium">{jardin.sol_ph}</span>
            </div>
          )}
          {jardin.sol_mo_pct && (
            <div>
              <span className="text-gray-500">MO:</span>{' '}
              <span className="font-medium">{jardin.sol_mo_pct}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Planches */}
      <div>
        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Sprout className="w-4 h-4 text-green-500" />
          Planches ({planches.length})
        </h3>
        {loading ? (
          <div className="text-center py-4">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {planches.map((planche) => (
              <div
                key={planche.id}
                className={`p-2 rounded-lg border text-center text-sm ${
                  planche.methode === 'msv'
                    ? 'bg-green-50 border-green-300'
                    : 'bg-gray-50 border-gray-300'
                }`}
              >
                <div className="font-bold">{planche.code}</div>
                <div className="text-xs text-gray-500">
                  {planche.famille_emoji || '🌱'}
                </div>
                <div className="text-xs">
                  {planche.methode === 'msv' ? '✅' : '🔧'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Badges */}
      {jardin.est_temoin && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
          🧪 <strong>Jardin témoin:</strong> Comparaison MSV vs travaillé
        </div>
      )}
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE CHECKLIST
// ═══════════════════════════════════════════════════════════════════════════

const VueChecklist = ({ jardins, jardinSelectionne, onSelectJardin, annee, onRefresh }) => {
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Charger les tâches du jardin sélectionné
  useEffect(() => {
    if (!jardinSelectionne) return;

    const loadTaches = async () => {
      setLoading(true);
      try {
        const data = await fetchAPI(`/jardins/${jardinSelectionne.id}/taches?annee=${annee}`);
        setTaches(data);
      } catch (err) {
        console.error('Erreur chargement tâches:', err);
        setTaches([]);
      } finally {
        setLoading(false);
      }
    };
    loadTaches();
  }, [jardinSelectionne?.id, annee]);

  // Toggle tâche
  const toggleTache = async (tacheId) => {
    try {
      const result = await fetchAPI(`/taches/${tacheId}/toggle`, { method: 'PUT' });
      setTaches((prev) =>
        prev.map((t) => (t.id === tacheId ? { ...t, statut: result.statut } : t))
      );
      onRefresh();
    } catch (err) {
      console.error('Erreur toggle tâche:', err);
    }
  };

  // Grouper par catégorie
  const categoriesConfig = {
    diagnostic: { nom: '🔬 Diagnostic', color: 'blue' },
    ressources: { nom: '📦 Ressources', color: 'orange' },
    couverture: { nom: '🌿 Couverture', color: 'green' },
    plantation: { nom: '🌱 Plantation', color: 'emerald' },
    suivi: { nom: '📊 Suivi', color: 'purple' },
    bilan: { nom: '📋 Bilan', color: 'gray' },
  };

  const tachesParCategorie = useMemo(() => {
    const grouped = {};
    taches.forEach((t) => {
      if (!grouped[t.categorie]) grouped[t.categorie] = [];
      grouped[t.categorie].push(t);
    });
    return grouped;
  }, [taches]);

  if (!jardinSelectionne) {
    return (
      <Card className="text-center py-12">
        <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 mb-4">Sélectionnez un jardin pour voir sa checklist</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {jardins.slice(0, 8).map((j) => (
            <button
              key={j.id}
              onClick={() => onSelectJardin(j)}
              className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium"
            >
              {j.code}
            </button>
          ))}
        </div>
      </Card>
    );
  }

  const totalTaches = taches.length;
  const tachesFaites = taches.filter((t) => t.statut === 'fait').length;
  const progression = totalTaches > 0 ? Math.round((tachesFaites / totalTaches) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sélecteur jardins */}
      <Card>
        <h3 className="font-semibold text-gray-700 mb-4">Jardins</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {jardins.map((j) => {
            const prog = j.taches_total > 0 ? Math.round((j.taches_faites / j.taches_total) * 100) : 0;
            return (
              <button
                key={j.id}
                onClick={() => onSelectJardin(j)}
                className={`w-full p-3 rounded-lg border text-left transition-all ${
                  jardinSelectionne?.id === j.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold">{j.code}</span>
                  <span className="text-sm text-gray-500">{prog}%</span>
                </div>
                <ProgressBar value={prog} max={100} height={4} />
              </button>
            );
          })}
        </div>
      </Card>

      {/* Checklist */}
      <div className="lg:col-span-3">
        <Card>
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center text-white text-xl font-bold">
                {jardinSelectionne.code}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Checklist {jardinSelectionne.nom}
                </h2>
                <p className="text-sm text-gray-500">Année {annee}</p>
              </div>
            </div>

            {/* Progression circulaire */}
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="35" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke="#22c55e"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${progression * 2.2} 220`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-green-600">{progression}%</span>
              </div>
            </div>
          </div>

          {/* Tâches par catégorie */}
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
            </div>
          ) : taches.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Aucune tâche pour ce jardin en {annee}</p>
              <button
                onClick={async () => {
                  await fetchAPI(`/jardins/${jardinSelectionne.id}/taches/init`, {
                    method: 'POST',
                    body: JSON.stringify({ annee }),
                  });
                  const data = await fetchAPI(`/jardins/${jardinSelectionne.id}/taches?annee=${annee}`);
                  setTaches(data);
                  onRefresh();
                }}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Initialiser la checklist {annee}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(categoriesConfig).map(([key, config]) => {
                const tachesCat = tachesParCategorie[key] || [];
                if (tachesCat.length === 0) return null;

                const faites = tachesCat.filter((t) => t.statut === 'fait').length;

                return (
                  <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 font-semibold text-gray-700 flex items-center justify-between">
                      <span>{config.nom}</span>
                      <span className="text-sm font-normal text-gray-500">
                        {faites}/{tachesCat.length}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {tachesCat.map((tache) => (
                        <div
                          key={tache.id}
                          onClick={() => toggleTache(tache.id)}
                          className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${
                            tache.statut === 'fait' ? 'bg-green-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          {tache.statut === 'fait' ? (
                            <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                          ) : (
                            <Circle className="w-6 h-6 text-gray-300 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <div
                              className={`font-medium ${
                                tache.statut === 'fait' ? 'text-green-700 line-through' : 'text-gray-800'
                              }`}
                            >
                              {tache.nom}
                            </div>
                            <div className="text-xs text-gray-500">{tache.description}</div>
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {tache.duree_heures < 1
                              ? `${tache.duree_heures * 60}min`
                              : `${tache.duree_heures}h`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE ROTATION
// ═══════════════════════════════════════════════════════════════════════════

const VueRotation = ({ jardins, familles, anneeActive }) => {
  const annees = [2026, 2027, 2028, 2029, 2030, 2031];
  const jardinsPleinChamp = jardins.filter((j) => j.type === 'plein_champ').slice(0, 4);

  // Générer rotation automatique basée sur ordre_rotation des familles
  const getRotation = (jardinIndex, annee) => {
    const offset = annee - 2026;
    const familleIndex = (jardinIndex + offset) % familles.length;
    return familles[familleIndex];
  };

  return (
    <Card>
      <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <RefreshCw className="w-6 h-6 text-green-600" />
        Rotation des Cultures (Cycle 6 ans)
      </h2>

      {/* Légende familles */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
        {familles.map((f) => (
          <div key={f.id} className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: `${f.couleur_hex}20` }}>
            <span>{f.emoji}</span>
            <span className="text-sm font-medium" style={{ color: f.couleur_hex }}>{f.nom}</span>
            <span className="text-xs text-gray-500">({f.niveau_gourmandise})</span>
          </div>
        ))}
      </div>

      {/* Tableau rotation */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-3 text-left font-semibold text-gray-700 border">Jardin</th>
              {annees.map((annee) => (
                <th key={annee} className={`p-3 text-center font-semibold border ${annee === anneeActive ? 'bg-green-100 text-green-700' : 'text-gray-700'}`}>
                  {annee}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jardinsPleinChamp.map((jardin, idx) => (
              <tr key={jardin.id} className="hover:bg-gray-50">
                <td className="p-3 border font-bold">{jardin.code}</td>
                {annees.map((annee) => {
                  const famille = getRotation(idx, annee);
                  return (
                    <td key={annee} className={`p-3 border text-center ${annee === anneeActive ? 'bg-green-50' : ''}`}>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-2xl">{famille?.emoji}</span>
                        <span className="text-xs font-medium" style={{ color: famille?.couleur_hex }}>
                          {famille?.nom?.substring(0, 8)}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Explication apports par famille */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {familles.map((f) => (
          <div key={f.id} className="p-4 rounded-lg border" style={{ borderColor: f.couleur_hex, backgroundColor: `${f.couleur_hex}05` }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{f.emoji}</span>
              <h4 className="font-bold" style={{ color: f.couleur_hex }}>{f.nom}</h4>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Gourmandise:</strong> {f.niveau_gourmandise}</p>
              <p><strong>Compost:</strong> {f.quantite_compost_kg_m2} kg/m²</p>
              {f.fixe_azote && <p className="text-green-600 font-medium">✨ Fixe l'azote !</p>}
              <p className="text-xs italic mt-2">{f.apport_recommande}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Principe rotation */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2">🔄 Principe de rotation</h3>
        <p className="text-sm text-blue-700">
          <strong>GOURMAND → LÉGUMINEUSES → MODÉRÉ</strong><br/>
          Les cucurbitacées épuisent le sol, les légumineuses rechargent l'azote gratuitement, 
          puis les brassicacées en profitent. Ne jamais remettre la même famille 2 ans de suite !
          Délai minimum: 3-4 ans entre 2 cultures de même famille.
        </p>
      </div>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE CULTURES
// ═══════════════════════════════════════════════════════════════════════════

const VueCultures = ({ cultures, familles }) => {
  const [filtreTexte, setFiltreTexte] = useState('');
  const [filtreFamille, setFiltreFamille] = useState('');
  const [filtreMSV1, setFiltreMSV1] = useState(false);
  const [cultureSelectionnee, setCultureSelectionnee] = useState(null);

  const culturesFiltrees = useMemo(() => {
    return cultures.filter((c) => {
      if (filtreTexte && !c.nom.toLowerCase().includes(filtreTexte.toLowerCase())) return false;
      if (filtreFamille && c.famille_id !== parseInt(filtreFamille)) return false;
      if (filtreMSV1 && !c.compatible_msv_annee1) return false;
      return true;
    });
  }, [cultures, filtreTexte, filtreFamille, filtreMSV1]);

  const getMoisNom = (num) => {
    const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    return mois[num - 1] || '';
  };

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <Card>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-48">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une culture..."
                value={filtreTexte}
                onChange={(e) => setFiltreTexte(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={filtreFamille}
            onChange={(e) => setFiltreFamille(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">Toutes les familles</option>
            {familles.map((f) => (
              <option key={f.id} value={f.id}>{f.emoji} {f.nom}</option>
            ))}
          </select>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filtreMSV1}
              onChange={(e) => setFiltreMSV1(e.target.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Compatible MSV année 1</span>
          </label>

          <span className="text-sm text-gray-500">{culturesFiltrees.length} cultures</span>
        </div>
      </Card>

      {/* Stats par famille */}
      <div className="grid grid-cols-6 gap-2">
        {familles.map((f) => {
          const count = cultures.filter((c) => c.famille_id === f.id).length;
          return (
            <button
              key={f.id}
              onClick={() => setFiltreFamille(filtreFamille === String(f.id) ? '' : String(f.id))}
              className={`p-3 rounded-lg border text-center transition-all ${
                filtreFamille === String(f.id) ? 'ring-2 ring-green-500' : ''
              }`}
              style={{ borderColor: f.couleur_hex, backgroundColor: `${f.couleur_hex}10` }}
            >
              <span className="text-2xl">{f.emoji}</span>
              <div className="text-xs font-medium mt-1" style={{ color: f.couleur_hex }}>{count}</div>
            </button>
          );
        })}
      </div>

      {/* Liste cultures */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {culturesFiltrees.map((culture) => (
          <Card 
            key={culture.id} 
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setCultureSelectionnee(culture)}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                style={{ backgroundColor: `${culture.famille_couleur}20` }}
              >
                {culture.famille_emoji}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{culture.nom}</h3>
                <p className="text-sm" style={{ color: culture.famille_couleur }}>
                  {culture.famille_nom}
                </p>
              </div>
              {culture.compatible_msv_annee1 && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  ✅ An 1
                </span>
              )}
            </div>

            {/* Calendrier visuel */}
            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
              {culture.mois_semis_debut && (
                <div className="p-2 bg-yellow-50 rounded text-center">
                  <div className="text-yellow-600 font-medium">🌱 Semis</div>
                  <div>{getMoisNom(culture.mois_semis_debut)}-{getMoisNom(culture.mois_semis_fin)}</div>
                </div>
              )}
              {culture.mois_plantation_debut && (
                <div className="p-2 bg-green-50 rounded text-center">
                  <div className="text-green-600 font-medium">🌿 Plant.</div>
                  <div>{getMoisNom(culture.mois_plantation_debut)}-{getMoisNom(culture.mois_plantation_fin)}</div>
                </div>
              )}
              {culture.mois_recolte_debut && (
                <div className="p-2 bg-orange-50 rounded text-center">
                  <div className="text-orange-600 font-medium">🧺 Récolte</div>
                  <div>{getMoisNom(culture.mois_recolte_debut)}-{getMoisNom(culture.mois_recolte_fin)}</div>
                </div>
              )}
            </div>

            {/* Conseils MSV */}
            {culture.conseils_msv && (
              <p className="mt-3 text-xs text-gray-600 italic line-clamp-2">💡 {culture.conseils_msv}</p>
            )}

            {/* Alertes */}
            <div className="mt-2 flex gap-2">
              {culture.sensible_limaces && (
                <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                  🐌 Limaces
                </span>
              )}
              {culture.necessite_semis_direct && (
                <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  🌱 Semis direct
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Modal détail culture */}
      {cultureSelectionnee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCultureSelectionnee(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{cultureSelectionnee.famille_emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{cultureSelectionnee.nom}</h2>
                    <p style={{ color: cultureSelectionnee.famille_couleur }}>{cultureSelectionnee.famille_nom}</p>
                  </div>
                </div>
                <button onClick={() => setCultureSelectionnee(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {cultureSelectionnee.varietes_recommandees && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-1">🌿 Variétés recommandées</h4>
                  <p className="text-sm text-green-700">{cultureSelectionnee.varietes_recommandees}</p>
                </div>
              )}

              {cultureSelectionnee.conseils_msv && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-1">💡 Conseils MSV</h4>
                  <p className="text-sm text-blue-700">{cultureSelectionnee.conseils_msv}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-500">Espacement rang:</span>
                  <span className="font-medium ml-2">{cultureSelectionnee.espacement_rang_cm} cm</span>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-500">Espacement plant:</span>
                  <span className="font-medium ml-2">{cultureSelectionnee.espacement_plant_cm} cm</span>
                </div>
                {cultureSelectionnee.plants_par_planche && (
                  <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                    <span className="text-gray-500">Plants/planche (20m²):</span>
                    <span className="font-medium ml-2">{cultureSelectionnee.plants_par_planche}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VUE MATIÈRES ORGANIQUES
// ═══════════════════════════════════════════════════════════════════════════

const VueMatieres = () => {
  const [matieres, setMatieres] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [matieresData, sourcesData] = await Promise.all([
          fetchAPI('/matieres'),
          fetchAPI('/sources'),
        ]);
        setMatieres(matieresData);
        setSources(sourcesData);
      } catch (err) {
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const matieresParType = useMemo(() => {
    const grouped = { compost: [], paillage: [], amendement: [], engrais_vert: [] };
    matieres.forEach((m) => {
      if (grouped[m.type]) grouped[m.type].push(m);
    });
    return grouped;
  }, [matieres]);

  const typeConfig = {
    compost: { label: '🌿 Composts', icon: '🌿' },
    paillage: { label: '🌾 Paillages', icon: '🌾' },
    amendement: { label: 'ite Amendements', icon: 'ite' },
    engrais_vert: { label: '🌱 Engrais verts', icon: '🌱' },
  };

  if (loading) {
    return (
      <Card className="text-center py-12">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
        <p className="text-gray-500 mt-2">Chargement...</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Résumé */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(typeConfig).map(([type, config]) => {
          const items = matieresParType[type] || [];
          return (
            <Card key={type} className="text-center">
              <span className="text-3xl">{config.icon}</span>
              <div className="text-2xl font-bold text-gray-800 mt-2">{items.length}</div>
              <div className="text-sm text-gray-500">{config.label}</div>
            </Card>
          );
        })}
      </div>

      {/* Matières par type */}
      {Object.entries(typeConfig).map(([type, config]) => {
        const items = matieresParType[type];
        if (!items || items.length === 0) return null;

        return (
          <Card key={type}>
            <h3 className="font-bold text-gray-800 mb-4 text-lg">{config.label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((m) => (
                <div key={m.id} className="p-4 bg-gray-50 rounded-lg border hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">{m.nom}</h4>
                    {m.gratuit_possible && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        💰 Gratuit
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    {m.rapport_cn_min && (
                      <p className="flex justify-between">
                        <span>Rapport C/N:</span>
                        <span className="font-medium">{m.rapport_cn_min}-{m.rapport_cn_max}</span>
                      </p>
                    )}
                    {m.epaisseur_cm_min && (
                      <p className="flex justify-between">
                        <span>Épaisseur:</span>
                        <span className="font-medium">{m.epaisseur_cm_min}-{m.epaisseur_cm_max} cm</span>
                      </p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${m.apport_azote === 'fort' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        N: {m.apport_azote}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${m.apport_carbone === 'fort' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                        C: {m.apport_carbone}
                      </span>
                    </div>
                    <p className="text-xs italic mt-2 text-gray-500">{m.usage_recommande}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      {/* Sources locales */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-500" />
            Sources locales ({sources.length})
          </h3>
        </div>

        {sources.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucune source enregistrée</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sources.map((s) => (
              <div key={s.id} className="p-4 border rounded-lg flex items-center gap-4 hover:bg-gray-50 transition-colors">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${s.gratuit ? 'bg-green-100' : 'bg-blue-100'}`}>
                  <Truck className={`w-6 h-6 ${s.gratuit ? 'text-green-600' : 'text-blue-600'}`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800">{s.nom}</h4>
                  <p className="text-sm text-gray-600">{s.matiere_nom} • {s.type_source}</p>
                  {s.disponibilite && <p className="text-xs text-gray-500">{s.disponibilite}</p>}
                  {s.contact_telephone && (
                    <p className="text-xs text-blue-600 mt-1">📞 {s.contact_telephone}</p>
                  )}
                </div>
                <div className="text-right">
                  {s.gratuit ? (
                    <span className="text-green-600 font-bold text-lg">GRATUIT</span>
                  ) : (
                    <span className="text-gray-700 font-medium">{s.prix_tonne}€/T</span>
                  )}
                  {s.distance_km && (
                    <p className="text-xs text-gray-500">📍 {s.distance_km} km</p>
                  )}
                  {s.livraison_possible && (
                    <p className="text-xs text-green-600">🚚 Livraison</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Guide rapide */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <h3 className="font-bold text-green-800 mb-3">📚 Guide rapide des apports MSV</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-semibold text-green-700">Année 1 - Démarrage</h4>
            <ul className="mt-1 space-y-1 text-green-600">
              <li>• Carton (occultation)</li>
              <li>• Foin/paille 30-40 cm</li>
              <li>• Compost si sol très pauvre</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-green-700">Années suivantes</h4>
            <ul className="mt-1 space-y-1 text-green-600">
              <li>• Paillage 10-15 cm entretien</li>
              <li>• Compost ciblé selon famille</li>
              <li>• Cendre pour liliacées/brassicacées</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default ModuleMSV;
